// Copyright (c) Bentley Systems
import * as React from "react";
import { ActionVm } from "../../Action/action-vm";
import IconByName from "../../Icons/IconByName";
import { TreeNodeData } from "../DataModels/tree-node-data";
import { findNodeById, getBase64IconSrc, isActionVisibleForNode, TreeNodeDataWithRaw } from "../etree-utils";

export type EtreeContextMenuState = {
  visible: boolean;
  x: number;
  y: number;
  nodeId: string | null;
};

type EtreeContextMenuProps = {
  contextMenu: EtreeContextMenuState;
  treeData: TreeNodeData[];
  itemActionsMap: Map<number, ActionVm>;
  expandedNodes: Record<string, boolean>;
  executeNodeAction: (node: TreeNodeDataWithRaw, wasNodeExpandedOverride?: boolean) => void;
  closeContextMenu: () => void;
  contextMenuRef: React.MutableRefObject<HTMLDivElement | null>;
};

const EtreeContextMenu = ({
  contextMenu,
  treeData,
  itemActionsMap,
  expandedNodes,
  executeNodeAction,
  closeContextMenu,
  contextMenuRef,
}: EtreeContextMenuProps) => {
  // Keyboard: Escape to close, ArrowUp/Down to navigate items
  React.useEffect(() => {
    if (!contextMenu.visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeContextMenu();
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const container = contextMenuRef.current;
        if (!container) return;
        const items = Array.from(container.querySelectorAll<HTMLButtonElement>("button"));
        if (items.length === 0) return;
        const focusedIndex = items.indexOf(document.activeElement as HTMLButtonElement);
        const delta = e.key === "ArrowDown" ? 1 : -1;
        const nextIndex = (focusedIndex + delta + items.length) % items.length;
        items[nextIndex]?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [contextMenu.visible, closeContextMenu, contextMenuRef]);

  // Move focus to first item when menu opens
  React.useEffect(() => {
    if (!contextMenu.visible) return;
    const frameId = requestAnimationFrame(() => {
      const firstButton = contextMenuRef.current?.querySelector<HTMLButtonElement>("button");
      firstButton?.focus();
    });
    return () => cancelAnimationFrame(frameId);
  }, [contextMenu.visible, contextMenuRef]);

  if (!contextMenu.visible) {
    return null;
  }

  const node = findNodeById(treeData, contextMenu.nodeId || "") as TreeNodeDataWithRaw;
  if (!node?.actionIds?.length) {
    return null;
  }

  const visibleActionIds = node.actionIds.filter((actionId) => {
    const action = itemActionsMap.get(actionId);
    if (!action) return false;
    return isActionVisibleForNode(action, node.rawData || node);
  });

  if (visibleActionIds.length === 0) {
    return null;
  }

  return (
    <div
      ref={(element) => {
        contextMenuRef.current = element;
      }}
      role="menu"
      style={{
        position: "fixed",
        top: contextMenu.y,
        left: contextMenu.x,
        backgroundColor: "var(--iui-color-background)",
        border: "1px solid var(--iui-color-border)",
        borderRadius: "4px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        zIndex: 1000,
        minWidth: "150px",
        color: "var(--iui-color-text)",
      }}
    >
      {visibleActionIds.map((actionId) => {
        const action = itemActionsMap.get(actionId);
        if (!action) return null;

        return (
          <button
            type="button"
            role="menuitem"
            key={actionId}
            style={{
              width: "100%",
              padding: "8px 16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              border: "none",
              textAlign: "left",
              backgroundColor: "var(--iui-color-background)",
              color: "var(--iui-color-text)",
            }}
            onClick={() => {
              executeNodeAction(
                { ...node, actionIds: [actionId] },
                expandedNodes[node.id] === true
              );
              closeContextMenu();
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--iui-color-background-hover)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "var(--iui-color-background)";
            }}
            onFocus={(e) => {
              e.currentTarget.style.backgroundColor = "var(--iui-color-background-hover)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.backgroundColor = "var(--iui-color-background)";
            }}
          >
            <div
              style={{
                width: "20px",
                height: "20px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {action.iconBase64Str ? (
                <img
                  src={getBase64IconSrc(action.iconBase64Str)}
                  alt=""
                  style={{ height: "24px", width: "24px", objectFit: "contain" }}
                />
              ) : action.icon ? (
                <IconByName
                  name={action.icon}
                  style={{
                    width: "20px",
                    height: "20px",
                    fill: "var(--iui-color-icon)",
                  }}
                />
              ) : null}
            </div>
            <span>{action.caption}</span>
          </button>
        );
      })}
    </div>
  );
};

export default EtreeContextMenu;
